// "Check coverage" modal flow — address form -> real AJAX check -> available/unavailable result,
// with an unavailable-path "Request a Callback" side-branch. Ported from twifi-theme-html's
// scripts/helpers/check-coverage-form.js + contact-us-form.js onto this theme's native dialog
// component (lib/dialog.js) and a real WordPress AJAX round trip (functions-ajax.php's
// amenities_check_coverage / amenities_coverage_callback_request) in place of the reference's
// client-side-only mock. Relies on the global `notify` (loaded via main.js) and `twf` (localized
// in functions.php's include_css_and_js()) the same way lib/payment-bank-transfer.js does.
import { openDialog, closeDialog } from './dialog';

function setLoading(button, loading) {
    button.disabled = loading;
    button.classList.toggle('loading', loading);

    if (loading) {
        button.setAttribute('aria-busy', 'true');
    } else {
        button.removeAttribute('aria-busy');
    }
}

function showError(message) {
    notify.error(message || twf.products_error_msg);
}

document.addEventListener('DOMContentLoaded', () => {
    const checkForm = document.querySelector('.check-coverage-form');
    const checkModal = document.getElementById('checkCoverageModal');
    const availableModal = document.getElementById('coverageAvailableModal');
    const unavailableModal = document.getElementById('coverageUnavailableModal');
    const addedToCartModal = document.getElementById('addedToCartModal');

    // WooCommerce's add-to-cart.js triggers this as a jQuery event on document.body (not a
    // native DOM event), via the WP-core `jquery` handle's global — bind through window.jQuery,
    // not this bundle's own imported jQuery module instance (main.js only copies plugin methods
    // onto window.jQuery, it doesn't unify the two), or this silently never fires.
    if (availableModal && addedToCartModal && window.jQuery) {
        window.jQuery(document.body).on('added_to_cart', (event, fragments, cartHash, $button) => {
            if (!$button || !$button.closest('#coverageAvailableModal').length) return;

            closeDialog(availableModal);
            openDialog(addedToCartModal);
        });
    }

    // A gated single-product page renders its CTA server-side (see
    // includes/woocommerce/single-product/single-product-hooks.php). A positive check is stored in
    // the WC session, so once the modal flow is over the page has to re-render to swap that CTA
    // back for WooCommerce's native add-to-cart form.
    const serverRenderedGate = document.querySelector('.product-hero__coverage');
    let coverageVerified = false;

    function refreshGateWhenFlowEnds() {
        if (!coverageVerified || !serverRenderedGate) return;

        // Deferred a tick: these modals are chained, so one closing usually means the next is
        // about to open. Reload only once nothing is left open.
        window.setTimeout(() => {
            if (document.querySelector('.dialog:not([hidden])')) return;
            window.location.reload();
        }, 0);
    }

    [availableModal, addedToCartModal].forEach((modal) => {
        if (modal) modal.addEventListener('dialog:close', refreshGateWhenFlowEnds);
    });

    // The modals are included per-template, so a gated CTA can end up on a page that doesn't carry
    // them — a button that opens nothing, with no other visible symptom.
    if (!checkModal && document.querySelector('[data-dialog-open="checkCoverageModal"]')) {
        // eslint-disable-next-line no-console
        console.warn(
            'coverage-check: a [data-dialog-open="checkCoverageModal"] trigger is present but #checkCoverageModal is not in the DOM — this template is missing the coverage modal includes.'
        );
    }

    if (checkForm && checkModal && availableModal && unavailableModal) {
        let currentPlanProductId = null;

        // The plan-card button that opened checkCoverageModal carries data-product-id (see
        // plan-card.twig) — dialog.js's openDialog() already forwards it as event.detail.trigger.
        checkModal.addEventListener('dialog:open', (event) => {
            const trigger = event.detail && event.detail.trigger;
            currentPlanProductId = trigger ? trigger.dataset.productId : null;
        });

        checkForm.addEventListener('submit', (event) => {
            event.preventDefault();

            const street = checkForm.querySelector('#coverage_street').value.trim();
            const houseNumber = checkForm.querySelector('#coverage_house_number').value.trim();
            const zip = checkForm.querySelector('#coverage_zip').value.trim();
            const submitButton = checkForm.querySelector('button[type="submit"]');

            setLoading(submitButton, true);

            fetch(twf.ajax_url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'amenities_check_coverage',
                    nonce: twf.coverage_nonce,
                    street,
                    house_number: houseNumber,
                    zip,
                }),
            })
                .then((response) => response.json())
                .then((data) => {
                    if (!data || !data.success) {
                        showError(data && data.data && data.data.message);
                        return;
                    }

                    const targetModal = data.data.available ? availableModal : unavailableModal;

                    if (data.data.available) coverageVerified = true;

                    targetModal.querySelectorAll('.coverage-result__address-text').forEach((el) => {
                        el.textContent = data.data.address;
                    });

                    if (targetModal === availableModal) {
                        const ctaButton = availableModal.querySelector('.coverage-result__cta-button');

                        if (ctaButton) {
                            if (currentPlanProductId) {
                                ctaButton.classList.add('add_to_cart_button', 'ajax_add_to_cart');
                                ctaButton.setAttribute('data-product_id', currentPlanProductId);
                            } else {
                                ctaButton.classList.remove('add_to_cart_button', 'ajax_add_to_cart');
                                ctaButton.removeAttribute('data-product_id');
                            }
                        }
                    }

                    closeDialog(checkModal);
                    openDialog(targetModal);
                })
                .catch(() => showError())
                .finally(() => setLoading(submitButton, false));
        });
    }

    const contactForm = document.querySelector('.contact-us-form');
    const contactModal = document.getElementById('contactUsModal');
    const successModal = document.getElementById('contactUsSuccessModal');

    if (contactForm && contactModal && successModal) {
        // Carry the already-checked address forward into the callback request.
        contactModal.addEventListener('dialog:open', () => {
            const addressText = unavailableModal
                ? unavailableModal.querySelector('.coverage-result__address-text')
                : null;
            const hidden = contactForm.querySelector('#callback_address');

            if (hidden && addressText) hidden.value = addressText.textContent;
        });

        contactForm.addEventListener('submit', (event) => {
            event.preventDefault();

            const submitButton = contactForm.querySelector('button[type="submit"]');

            setLoading(submitButton, true);

            fetch(twf.ajax_url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'amenities_coverage_callback_request',
                    nonce: twf.coverage_nonce,
                    name: contactForm.querySelector('#name').value.trim(),
                    phone: contactForm.querySelector('#phone').value.trim(),
                    address: contactForm.querySelector('#callback_address').value,
                }),
            })
                .then((response) => response.json())
                .then((data) => {
                    if (!data || !data.success) {
                        showError(data && data.data && data.data.message);
                        return;
                    }

                    closeDialog(contactModal);
                    openDialog(successModal);
                    contactForm.reset();
                })
                .catch(() => showError())
                .finally(() => setLoading(submitButton, false));
        });
    }
});
